import { createFileRoute } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { object, string, InferType, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import useFeatureFlags from "@/lib/featureFlags/useFeatureFlags";
import { useEffect, useCallback, useMemo } from "react";
import { Button } from "@heroui/react";
import { useTour } from "@/lib/tour/useTour";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion, faPlay } from "@fortawesome/free-solid-svg-icons";
import { driverObj } from "@/lib/tour/driverConfig";
import CheckboxInput from "@/components/inputs/CheckboxInput";

function RouteComponent() {
    const { user, logout, refreshUser } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { languagePreferenceEnabled } = useFeatureFlags();
    const { t, i18n } = useTranslation();
    const { endTour } = useTour();

    const cleanupListener = useCallback(() => {
        endTour();
    }, [endTour]);

    useEffect(() => {
        return () => {
            document.removeEventListener("driver:destroy", cleanupListener);
        };
    }, [cleanupListener]);

    const handleStartTour = () => {
        document.removeEventListener("driver:destroy", cleanupListener);
        driverObj.drive();
        document.addEventListener("driver:destroy", cleanupListener);
    };

    const schema = useMemo(
        () =>
            object().shape({
                first_name: string()
                    .required()
                    .label(
                        t("validation.first_name", { ns: "accountSettings" }),
                    ),
                last_name: string()
                    .required()
                    .label(
                        t("validation.last_name", { ns: "accountSettings" }),
                    ),
                phone_number: string()
                    .nullable()
                    .label(
                        t("validation.phone_number", { ns: "accountSettings" }),
                    ),
                email: string()
                    .email(
                        t("validation.invalid_email", {
                            ns: "accountSettings",
                        }),
                    )
                    .required()
                    .label(t("validation.email", { ns: "accountSettings" })),
                address_1: string()
                    .nullable()
                    .label(
                        t("validation.address_1", { ns: "accountSettings" }),
                    ),
                address_2: string()
                    .optional()
                    .nullable()
                    .label(
                        t("validation.address_2", { ns: "accountSettings" }),
                    ),
                city: string()
                    .required()
                    .label(t("validation.city", { ns: "accountSettings" })),
                state: string()
                    .required()
                    .label(t("validation.state", { ns: "accountSettings" })),
                zip: string()
                    .required()
                    .label(t("validation.zip_code", { ns: "accountSettings" })),
                language_preference: string()
                    .required()
                    .label(
                        t("validation.language_preference", {
                            ns: "accountSettings",
                        }),
                    ),
                date_format: string()
                    .oneOf([
                        "month_day_year",
                        "day_month_year",
                        "year_month_day",
                    ])
                    .required()
                    .label(
                        t("validation.date_preference", {
                            ns: "accountSettings",
                        }),
                    ),
                unit_preference: string()
                    .oneOf(["imperial", "metric"])
                    .required()
                    .label(
                        t("validation.unit_preference", {
                            ns: "accountSettings",
                        }),
                    ),
                aggregated_data_sharing: boolean()
                    .required()
                    .label(
                        t("validation.aggregated_data_sharing", {
                            ns: "accountSettings",
                        }),
                    ),
            }),
        [t],
    );

    type FormData = InferType<typeof schema>;

    const form = useForm<FormData>({
        resolver: yupResolver(schema),
    });

    const { data: userData } = useGetRequest<FormData>({
        url: `users/${user?.id}/`,
        enabled: !!user?.id,
    });

    const watchedLanguage = form.watch("language_preference");

    const updateAccountRequest = useMutateRequest({
        url: `users/${user?.id}/`,
        method: "put",
        successMessage: t("update_successful", { ns: "accountSettings" }),
        errorMessage: t("failed_to_update", { ns: "accountSettings" })
    });

    const updateLanguageRequest = useMutateRequest({
        url: `users/${user?.id}/`,
        method: "patch",
    });

    useEffect(() => {
        if (userData) {
            form.reset({
                ...userData,
                language_preference: i18n.language,
            });
        }
    }, [userData, form, i18n.language]);

    useEffect(() => {
        if (!user) return;
        if (watchedLanguage && watchedLanguage !== i18n.language) {
            i18n.changeLanguage(watchedLanguage);

            updateLanguageRequest.mutate(
                { data: { language_preference: watchedLanguage } },
                {
                    onSuccess: () => {
                        refreshUser();
                    },
                },
            );
        }
    }, [watchedLanguage, i18n, updateLanguageRequest, refreshUser, user]);

    const onSubmit = useCallback(
        (data: FormData) => {
            updateAccountRequest.mutate(
                { data },
                {
                    onSuccess: () => {
                        form.reset({
                            ...data,
                            language_preference: i18n.language,
                        });
                        refreshUser();
                    },
                },
            );
        },
        [updateAccountRequest, form, i18n, refreshUser],
    );

    return (
        <FormProvider {...form}>
            <div className="flex flex-col gap-4 max-w-2xl px-4 pt-2">
                <h1 className="text-2xl font-bold">
                    {t("account_settings", { ns: "common" })}
                </h1>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                >
                    <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6">
                        <TextInput
                            name="first_name"
                            label={t("first_name", { ns: "common" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="last_name"
                            label={t("last_name", { ns: "common" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="phone_number"
                            label={t("phone_number", { ns: "common" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="email"
                            label={t("email", { ns: "common" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="address_1"
                            label={t("address_1", { ns: "accountSettings" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="address_2"
                            label={t("address_2", { ns: "accountSettings" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="city"
                            label={t("city", { ns: "accountSettings" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="state"
                            label={t("state", { ns: "accountSettings" })}
                            size="sm"
                            labelPlacement="outside"
                        />
                        <TextInput
                            name="zip"
                            label={t("zip_code", { ns: "accountSettings" })}
                            size="sm"
                            labelPlacement="outside"
                        />

                        {languagePreferenceEnabled && (
                            <>
                                <SelectInput
                                    name="language_preference"
                                    label={t("language_preference", {
                                        ns: "accountSettings",
                                    })}
                                    options={[
                                        {
                                            id: "en",
                                            description: "English",
                                        },
                                        {
                                            id: "pt",
                                            description: "Portuguese",
                                        },
                                    ]}
                                    size="sm"
                                    labelPlacement="outside"
                                />

                                <SelectInput
                                    name="date_format"
                                    label={t("date_preference", {
                                        ns: "accountSettings",
                                    })}
                                    options={[
                                        {
                                            id: "month_day_year",
                                            description: t(
                                                "date_formats.month_day_year",
                                                { ns: "accountSettings" },
                                            ),
                                        },
                                        {
                                            id: "day_month_year",
                                            description: t(
                                                "date_formats.day_month_year",
                                                { ns: "accountSettings" },
                                            ),
                                        },
                                        {
                                            id: "year_month_day",
                                            description: t(
                                                "date_formats.year_month_day",
                                                { ns: "accountSettings" },
                                            ),
                                        },
                                    ]}
                                    size="sm"
                                    labelPlacement="outside"
                                />
                                <SelectInput
                                    name="unit_preference"
                                    label={t("unit_preference", {
                                        ns: "accountSettings",
                                    })}
                                    options={[
                                        {
                                            id: "imperial",
                                            description: t(
                                                "unit_formats.imperial",
                                                { ns: "accountSettings" },
                                            ),
                                        },
                                        {
                                            id: "metric",
                                            description: t(
                                                "unit_formats.metric",
                                                { ns: "accountSettings" },
                                            ),
                                        },
                                    ]}
                                    size="sm"
                                    labelPlacement="outside"
                                />
                            </>
                        )}
                    </div>
                    <div>
                        <CheckboxInput
                            name="aggregated_data_sharing"
                            label={t("label_aggregated_data_sharing", {
                                ns: "accountSettings",
                            })}
                            size="sm"
                        />
                    </div>
                    <Button
                        type="submit"
                        color="primary"
                        isLoading={updateAccountRequest.isPending}
                        className="mt-4"
                    >
                        {t("save_changes", { ns: "common" })}
                    </Button>
                </form>

                <div className="flex flex-col gap-4">
                    <Button color="danger" onPress={logout}>
                        {t("log_out", { ns: "common" })}
                    </Button>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                                icon={faCircleQuestion}
                                className="text-primary-500"
                            />
                            <span className="font-medium">
                                {t("need_help", { ns: "accountSettings" })}
                            </span>
                        </div>
                        <Button
                            color="primary"
                            variant="flat"
                            onPress={handleStartTour}
                            startContent={<FontAwesomeIcon icon={faPlay} />}
                        >
                            {t("start_tour", { ns: "accountSettings" })}
                        </Button>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
}

export const Route = createFileRoute("/dashboard/account-settings/account")({
    component: RouteComponent,
});
